import { differenceInMilliseconds, isAfter, isValid } from 'date-fns';
import { FC } from 'react';

import { Text } from '../../../cdl/Text/Text';
import { Tooltip } from '../../../cdl/Tooltip/Tooltip';
import { formatDate } from '../../../common/helpers/formatDate.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { MILLISECONDS_IN_DAY, MILLISECONDS_IN_HOUR, MILLISECONDS_IN_MINUTE } from '../../../common/time.constants';

interface Props {
    dateMeasured: string;
}

const timeZonePattern = /(Z|[+-]\d{2}:?\d{2})$/;

const parseUtcDate = (value: string): Date => new Date(timeZonePattern.test(value) ? value : `${value}Z`);

export const DateMeasured: FC<Props> = ({ dateMeasured }: Props) => {
    const timeSince = (dateMeasured: string): string | null => {
        const utcDateMeasured = parseUtcDate(dateMeasured);

        if (!isValid(utcDateMeasured)) {
            return null;
        }

        if (isAfter(utcDateMeasured, new Date())) {
            return formatDate({
                date: dateMeasured,
                timeZone: 'UTC',
            });
        }

        const duration = differenceInMilliseconds(new Date(), utcDateMeasured);

        let interval = Math.floor(duration / MILLISECONDS_IN_DAY);
        if (interval === 1) {
            return translate('order.stockbox.day', { day: interval.toString() });
        }
        if (interval >= 1) {
            return translate('order.stockbox.days', { days: interval.toString() });
        }
        interval = Math.floor(duration / MILLISECONDS_IN_HOUR);
        if (interval === 1) {
            return translate('order.stockbox.hour', { hour: interval.toString() });
        }
        if (interval >= 1) {
            return translate('order.stockbox.hours', { hours: interval.toString() });
        }
        interval = Math.floor(duration / MILLISECONDS_IN_MINUTE);
        if (interval === 1) {
            return translate('order.stockbox.minute', { minute: interval.toString() });
        }
        if (interval >= 1) {
            return translate('order.stockbox.minutes', { minutes: interval.toString() });
        }

        return null;
    };

    const timeSinceDate = timeSince(dateMeasured);

    if (!timeSinceDate) {
        return null;
    }

    return (
        <Tooltip
            label={translate('order.stockbox.lastUpdatedTooltip', {
                date: formatDate({ date: dateMeasured, timeZone: 'UTC' }),
            })}
        >
            <Text variant="extraSmall" color="foreground.subtle" data-testid="info-label">
                {timeSinceDate}
            </Text>
        </Tooltip>
    );
};
